// Tiny HTTP client for the Link API.
//
// All routes go through here so the bearer token, idempotency key,
// and error envelope are handled in one place. The token lives in
// sessionStorage during use. An HttpOnly cookie can resume the same valid
// session after restart; private identity keys remain in the IndexedDB store.

import type { ApiError } from "@link/shared";
import { beginUpdateActivity } from "./update-activity";

const TOKEN_KEY = "link.session.token";
const SESSION_NOTICE_KEY = "link.session.notice";
const SIGNED_OUT_KEY = "link.session.signed-out";
export const ACCOUNT_SUSPENDED_MESSAGE = "Your account has been suspended.";
let sessionInvalidationQueued = false;

export function readSessionToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage.getItem(TOKEN_KEY);
  } catch {
    // Some private-browsing and hardened-storage configurations deny access.
    // Treat that as signed out so the application can still render auth UI.
    return null;
  }
}

export function writeSessionToken(token: string): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(TOKEN_KEY, token);
    window.localStorage?.removeItem(SIGNED_OUT_KEY);
  } catch {
    throw new Error(
      "This browser is blocking Link's sign-in session. Allow site storage or leave private browsing, then try again.",
    );
  }
}

export function clearSessionToken(): void {
  if (typeof window === "undefined") return;
  // Non-secret tombstone: an offline sign-out must not silently resume later.
  // Only a new, explicit successful sign-in clears it.
  try { window.localStorage?.setItem(SIGNED_OUT_KEY, "1"); } catch { /* storage unavailable */ }
  try {
    window.sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // There is nothing useful to clear when storage itself is unavailable.
  }
}

export function sessionResumeBlocked(): boolean {
  if (typeof window === "undefined") return true;
  try { return window.localStorage?.getItem(SIGNED_OUT_KEY) === "1"; }
  catch { return true; }
}

/** Read and remove the one-shot message shown after a forced sign-out. */
export function consumeSessionNotice(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const notice = window.sessionStorage.getItem(SESSION_NOTICE_KEY);
    window.sessionStorage.removeItem(SESSION_NOTICE_KEY);
    return notice;
  } catch {
    return null;
  }
}

/** Immediately leave the app when moderation suspends the signed-in account. */
export function terminateSuspendedSession(): void {
  if (typeof window === "undefined") return;
  clearSessionToken();
  try {
    window.sessionStorage.setItem(SESSION_NOTICE_KEY, ACCOUNT_SUSPENDED_MESSAGE);
  } catch {
    // The inline login response still explains the suspension when storage is
    // unavailable; there is simply no one-shot message after this reload.
  }
  queueSessionReload();
}

export interface FetchOpts {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Defaults to the in-memory session token; pass `null` for unauth. */
  token?: string | null;
  /** Optional idempotency key for write endpoints. */
  idempotencyKey?: string;
  /** Optional fresh-signature headers for identity-bearing mutations. */
  freshSignature?: { signatureB64: string; challengeB64: string } | null;
  /** Abort signal forwarded to fetch. */
  signal?: AbortSignal;
  /** Cache policy forwarded to fetch. Version checks use `no-store`. */
  cache?: RequestCache;
}

/** Thrown by `fetchJson` for any non-2xx response. */
export class ApiCallError extends Error {
  constructor(
    public readonly status: number,
    public readonly envelope: ApiError | null,
  ) {
    super(envelope?.message ?? `HTTP ${status}`);
    this.name = "ApiCallError";
  }
}

export async function fetchJson<T>(
  path: string,
  opts: FetchOpts = {},
): Promise<T> {
  const finish = opts.method && opts.method !== "GET" ? beginUpdateActivity() : () => {};
  try { return await performFetchJson<T>(path, opts); }
  finally { finish(); }
}

async function performFetchJson<T>(path: string, opts: FetchOpts): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };

  const token = opts.token === undefined ? readSessionToken() : opts.token;
  if (token !== null && token.length > 0) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  if (opts.idempotencyKey !== undefined) {
    headers["Idempotency-Key"] = opts.idempotencyKey;
  }
  if (opts.freshSignature !== undefined && opts.freshSignature !== null) {
    headers["X-Link-Fresh-Signature"] = opts.freshSignature.signatureB64;
    headers["X-Link-Fresh-Challenge"] = opts.freshSignature.challengeB64;
  }

  const res = await fetch(path, {
    method: opts.method ?? "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
    cache: opts.cache,
  });

  if (!res.ok) {
    let envelope: ApiError | null = null;
    try {
      envelope = (await res.json()) as ApiError;
    } catch {
      // Body wasn't JSON; surface a synthetic envelope.
    }
    invalidateRejectedSession(res.status, envelope, token);
    throw new ApiCallError(res.status, envelope);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/** Return to sign-in when a stored bearer token is no longer valid. */
function invalidateRejectedSession(
  status: number,
  envelope: ApiError | null,
  token: string | null,
): void {
  if (
    status === 403 &&
    envelope?.code === "auth.account_suspended" &&
    token !== null &&
    token.length > 0
  ) {
    if (readSessionToken() !== token) return;
    terminateSuspendedSession();
    return;
  }
  if (
    status !== 401 ||
    envelope?.code !== "auth.unauthenticated" ||
    token === null ||
    token.length === 0 ||
    typeof window === "undefined"
  ) {
    return;
  }

  // A late rejection from a previous account must not sign out a newer one.
  if (readSessionToken() !== token) return;
  clearSessionToken();
  queueSessionReload();
}

function queueSessionReload(): void {
  if (typeof window === "undefined") return;
  if (sessionInvalidationQueued) return;
  sessionInvalidationQueued = true;
  window.setTimeout(() => window.location.reload(), 0);
}

/** Send multipart form data while preserving the same bearer/error handling. */
export async function fetchForm<T>(path: string, form: FormData): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json" };
  const token = readSessionToken();
  if (token !== null && token.length > 0) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(path, { method: "POST", headers, body: form });
  if (!res.ok) {
    let envelope: ApiError | null = null;
    try {
      envelope = (await res.json()) as ApiError;
    } catch {
      // The normal API envelope was unavailable.
    }
    invalidateRejectedSession(res.status, envelope, token);
    throw new ApiCallError(res.status, envelope);
  }
  return (await res.json()) as T;
}

/** Convenience: open a WebSocket against `/ws` carrying the bearer token. */
export function openSessionSocket(): WebSocket {
  const token = readSessionToken();
  // Browsers can't set Authorization headers on WebSocket — use a
  // query-string param the API gateway will consume.
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  const url = `${protocol}//${window.location.host}/ws${
    token !== null && token.length > 0
      ? `?token=${encodeURIComponent(token)}`
      : ""
  }`;
  return new WebSocket(url);
}
